/**
 * replayFailure — disposition of ONE offline-queue entry whose replay handler
 * threw. The "what happens to a failed entry this flush cycle" layer, split
 * from the offlineQueue engine (decompose-by-cohesion, keeps the engine under
 * the file-size gate) and given its own tests.
 *
 * Four outcomes, decided by retry classification (retryClassification.ts):
 *   0. 401 after serviceFetch's one re-handoff → 'auth_required': kept PENDING, no retry spent,
 *      the flush stops for this cycle; sent by the first flush after the rep logs in again.
 *   1. ConflictError            → status 'conflict', fan a ConflictEvent out.
 *   2. FINAL (every 4xx but 401/408/429 — 400 403 404 409 422 … — / ContractMismatch / a
 *      FinalRefusalError a handler threw for a
 *      refusal its door declares final by CODE / a deterministic upstream cause
 *      behind a transient outer status — a 502 …_RPC_FAILED wrapping a Postgres
 *      23514, BUG-2026-07-21-S1787) → fail LOUD NOW: pin 'failed' at maxRetries +
 *      capture the payload off-device with reason 'non-retryable-4xx'. It would
 *      fail identically on every replay, so retrying to exhaustion only DELAYS a
 *      guaranteed loss and hides it for 5 silent cycles — or, for S1787, loops it
 *      forever (BUG-FE-OFFLINE-RETRY-DETERMINISTIC-SILENT-LOSS, D-33 write-path).
 *   3. TRANSIENT (5xx/network/timeout/408/429) → retries++, staying pending until
 *      maxRetries, then pin 'failed' + capture 'offline-retries-exhausted'. Each retry is
 *      rescheduled (`nextAttemptAt`: exponential backoff with jitter, Retry-After honoured), and
 *      the FIRST failed replay now also captures the payload off-device with
 *      reason 'first-replay-failure' (operator 2026-07-12,
 *      FR-FE-DURABLE-CAPTURE-AT-ENQUEUE: "the capture must fire at the first
 *      failure, not the fifth"). A 502 is transient, so the dead-write-path
 *      incident kept the ONLY copy of two visit reports on a rep's phone.
 */

import { captureFailedWriteRetained } from './failedWriteCapture'
import { isRetryable, isAuthRequired, httpStatusOf, refusalCodeOf } from './retryClassification'
import { retryAfterSecondsOf } from '../http/httpHelpers'
import { ConflictError, FinalRefusalError, type QueueEntry, type ConflictEvent } from './offlineQueueTypes'

export interface ReplayFailureOutcome {
  /** True when the entry transitioned to a genuinely-failed state this cycle. */
  failed: boolean
  /** Set when the handler signalled a server-side conflict (fan out to listeners). */
  conflict?: ConflictEvent
  /**
   * Set when the door answered 401 after serviceFetch's one re-handoff: the session is gone,
   * so the flush offers NOTHING more this cycle (every further entry would meet the same 401).
   */
  authRequired?: true
}

/** First retry delay after a transient failure. The flush interval is 30 s, so this is one cycle. */
export const RETRY_BACKOFF_BASE_MS = 30_000
/** Ceiling of the exponential delay (a server Retry-After may still ask for longer — it is honoured). */
export const RETRY_BACKOFF_CAP_MS = 15 * 60_000
/**
 * Jitter fraction: the delay is drawn from [nominal*(1-J), nominal]. Kept below 0.5 so the
 * shortest draw of attempt n+1 (2*nominal*(1-J)) still exceeds the longest of attempt n — the
 * delays strictly grow until the cap, and entries that failed together spread out.
 */
export const RETRY_BACKOFF_JITTER = 0.25

/** Inputs of one retry-delay computation. `random` is injected so tests can seed it. */
export interface RetryDelayInput {
  /** Retries spent INCLUDING the failure being scheduled (1 after the first failure). */
  retriesSoFar: number
  /** The server's Retry-After for this failure, in seconds; undefined when none was sent. */
  retryAfterSeconds: number | undefined
  random: () => number
}

/**
 * The delay before the next attempt: exponential (base * 2^(n-1), capped) with jitter, and
 * never shorter than the server's Retry-After. Pure — the RNG is injected.
 */
export function retryDelayMs({ retriesSoFar, retryAfterSeconds, random }: RetryDelayInput): number {
  const exponent = Math.max(0, retriesSoFar - 1)
  const nominal = Math.min(RETRY_BACKOFF_CAP_MS, RETRY_BACKOFF_BASE_MS * 2 ** exponent)
  const jittered = Math.floor(nominal * (1 - RETRY_BACKOFF_JITTER * random()))
  const floor = retryAfterSeconds === undefined ? 0 : retryAfterSeconds * 1000
  return Math.max(jittered, floor)
}

/** Clock + RNG seams of applyReplayFailure (tests inject both; production uses the real ones). */
export interface ReplayFailureDeps {
  now?: number
  random?: () => number
}

function errToString(err: unknown): string {
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err)
}

/**
 * Disposition of a DEPENDENT entry whose predecessor is terminally failed
 * (queueOrdering.predecessorVerdict → 'failed'). It was never offered to the server and
 * must not be: the write it supplements does not exist. It takes the SAME terminal shape
 * a replay failure produces — pinned `failed` at maxRetries, KEPT in the queue (critical
 * entries are never purged), surfaced by the failed count / unsynced list, discardable —
 * and its payload is captured off-device under the registration's `reasonCode`.
 *
 * Pinning flips the entry out of `pending`, so the flush never reaches it again: the
 * capture fires exactly once per pin, with no dedupe flag.
 */
export function applyPredecessorFailure(
  entry: QueueEntry,
  predecessor: QueueEntry,
  reasonCode: string,
  isCriticalEntry: boolean,
): void {
  entry.retries = entry.maxRetries
  entry.status = 'failed'
  entry.failureReason = 'predecessor-failed'
  if (isCriticalEntry) {
    // The capture primitive hoists the payload's source_event_id to the top level (R4).
    void captureFailedWriteRetained(entry.type, entry.payload, reasonCode, {
      id: entry.id,
      retries: entry.retries,
      createdAt: entry.createdAt,
      predecessor_id: predecessor.id,
      predecessor_type: predecessor.type,
      predecessor_failure_reason: predecessor.failureReason,
    })
  }
}

/**
 * Apply the failure disposition to `entry` IN PLACE (mutates status/retries/
 * lastAttempt) and, for a critical entry, capture the payload off-device.
 * Returns whether it counts as failed + any conflict event to fan out.
 *
 * @param isCriticalEntry caller-computed criticality (offlineQueue owns the
 *   EPHEMERAL_QUEUE_TYPES set; passing the boolean avoids an import cycle).
 */
export function applyReplayFailure(
  entry: QueueEntry,
  err: unknown,
  isCriticalEntry: boolean,
  { now = Date.now(), random = Math.random }: ReplayFailureDeps = {},
): ReplayFailureOutcome {
  // Server data is newer — a conflict, not a failure. Never retried here.
  if (err instanceof ConflictError) {
    entry.status = 'conflict'
    entry.failureReason = 'conflict'
    return {
      failed: false,
      conflict: { entry, serverTimestamp: err.serverTimestamp, localTimestamp: err.localTimestamp },
    }
  }

  entry.lastAttempt = new Date(now).toISOString()

  // LOG IN AGAIN — a 401 that outlived serviceFetch's one re-handoff (which already raised the
  // session-expired overlay). Not final, not a transient retry: the entry stays PENDING, spends
  // no retry, is not captured as a failure, and waits for the first flush after the login (the
  // outbox survives the logout). The flush stops offering further entries this cycle.
  if (!(err instanceof FinalRefusalError) && isAuthRequired(err)) {
    entry.failureReason = 'auth_required'
    delete entry.nextAttemptAt
    return { failed: false, authRequired: true }
  }

  // DETERMINISTIC failure — fail LOUD immediately, never silent-retry to
  // exhaustion. Pin at maxRetries so it is a genuine failure (surfaced by
  // getFailedCount, retained for criticals, manually retryable via
  // retryFailedEntries once the rep corrects the input). A FinalRefusalError is the door's own
  // declaration that its answer cannot change (by code, e.g. the v8 door's SL #2682 refusals):
  // terminal whatever its status, which the generic rule alone would keep retrying.
  if (err instanceof FinalRefusalError || !isRetryable(err)) {
    entry.retries = entry.maxRetries
    entry.status = 'failed'
    delete entry.nextAttemptAt
    // Tag WHY. This is what lets queueExit refuse to offer a pointless retry — and
    // what makes the exhaustive terminal-state suite able to enumerate the fates.
    entry.failureReason = 'non-retryable-4xx'
    // The server's verdict rides with the pin, so the rep's surface can say WHY it is never re-sent.
    entry.finalCode = refusalCodeOf(err)
    if (isCriticalEntry) {
      // Retain-on-unconfirmed: the entry is already pinned failed + KEPT for a critical
      // (queueStore.isCritical), so a durable-sink failure never loses it — but it is now
      // surfaced LOUDLY instead of silently swallowed (D-33 R1). Fire-and-forget: the
      // disposition is decided synchronously above; the off-device copy is best-effort.
      void captureFailedWriteRetained(entry.type, entry.payload, 'non-retryable-4xx', {
        id: entry.id,
        status: httpStatusOf(err),
        retries: entry.retries,
        createdAt: entry.createdAt,
        error: errToString(err),
      })
    }
    return { failed: true }
  }

  // TRANSIENT failure — retry up to maxRetries, each attempt RESCHEDULED (AC 8): exponential
  // backoff with jitter, the server's Retry-After as the floor, so a 429/5xx is never hammered
  // on every 30 s flush. The replay re-sends the stored body verbatim — same source_event_id.
  entry.retries++
  if (entry.retries >= entry.maxRetries) {
    entry.status = 'failed'
    entry.failureReason = 'retries-exhausted'
    delete entry.nextAttemptAt
    if (isCriticalEntry) {
      // Last-resort durable capture — a critical write that exhausted its
      // retries never reached the server (operator 2026-06-21: never lose a
      // report). Reason distinguishes it from the deterministic path above.
      void captureFailedWriteRetained(entry.type, entry.payload, 'offline-retries-exhausted', {
        id: entry.id,
        retries: entry.retries,
        createdAt: entry.createdAt,
        error: errToString(err),
      })
    }
    return { failed: true }
  }

  const delay = retryDelayMs({ retriesSoFar: entry.retries, retryAfterSeconds: retryAfterSecondsOf(err), random })
  entry.nextAttemptAt = new Date(now + delay).toISOString()

  // FIRST replay failure — capture the payload off-device NOW (operator
  // 2026-07-12: "the capture must fire at the FIRST failure, not the fifth").
  // A 502 (the dead-RPC incident) classifies as transient, so the old code kept
  // the ONLY copy of the report in the rep's IndexedDB for 5 cycles — and if the
  // device never came back online, forever. Retry cadence is untouched; this is
  // purely an extra durable copy on the way.
  //
  // `retries` is MONOTONIC within a flush lifecycle, so `=== 1` fires EXACTLY
  // ONCE across every 30s flush — no dedupe flag, and therefore NO new field on
  // QueueEntry (a new REQUIRED field would orphan the unrecovered field data
  // already sitting in the live on-device queue).
  if (isCriticalEntry && entry.retries === 1) {
    void captureFailedWriteRetained(entry.type, entry.payload, 'first-replay-failure', {
      id: entry.id,
      retries: entry.retries,
      createdAt: entry.createdAt,
      error: errToString(err),
    })
  }

  return { failed: false }
}
